/**
 * Coupons Page
 *
 * Manifold coupons for the current clinic: create, edit, archive.
 *
 * Coupons are a flat list with their own fields (code, discount, duration,
 * expiry, redemption cap, price scope), so they get their own page rather than
 * a branch of the family → item → price tree on the catalog page.
 *
 * Editing follows the same draft/release model as pricing: a change is staged
 * locally and only reaches Manifold when a release is published. Coupons are
 * only editable on clinics whose catalog is served by the changeset API — the
 * legacy deployment has no coupon write path.
 */

import { useState, useEffect, useCallback, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { manifoldAPI } from '../services/api'
import useDraftStore from '../stores/draftStore'
import {
  Card, CardContent,
  Button, Input, Label,
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, Plus, Pencil, Archive, ArchiveRestore, Tag,
  ChevronRight, ChevronDown, Users, Info,
} from 'lucide-react'

const DISCOUNT_TYPES = ['percentage', 'fixed_amount']
const DURATION_TYPES = ['one_time', 'forever', 'limited_period']
const PERIOD_UNITS = ['day', 'week', 'month', 'year']

const DURATION_LABEL = {
  one_time: 'First invoice',
  forever: 'Every invoice',
  limited_period: 'Limited',
}

function formatDiscount(coupon) {
  if (coupon.discount_type === 'percentage') {
    const percent = coupon.discount_percentage ?? coupon.percentage ?? coupon.discount_percent
    return percent != null ? `${percent}%` : '—'
  }
  const amount = coupon.discount_amount
  return amount != null ? `$${(amount / 100).toFixed(2)}` : '—'
}

function formatDuration(coupon) {
  if (coupon.duration_type === 'limited_period') {
    const unit = coupon.period_unit || 'month'
    return `${coupon.period ?? '?'} ${unit}${coupon.period === 1 ? '' : 's'}`
  }
  return DURATION_LABEL[coupon.duration_type] || coupon.duration_type || '—'
}

/** Coupons store expiry as epoch milliseconds (see Manifold's coupons_util). */
function formatExpiry(coupon) {
  const raw = coupon.valid_till ?? coupon.expires_at ?? coupon.redeem_by ?? coupon.valid_until
  if (raw == null) return 'Never'
  const date = new Date(Number(raw))
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString()
}

function formatRedemptions(coupon) {
  const used = coupon.redemptions ?? 0
  return coupon.max_redemptions != null ? `${used} / ${coupon.max_redemptions}` : `${used}`
}

function dateToMillis(value) {
  if (!value) return null
  // <input type="date"> gives YYYY-MM-DD; treat it as end of that day UTC so a
  // coupon valid "until the 30th" does not expire at midnight on the 30th.
  const parsed = Date.parse(`${value}T23:59:59.999Z`)
  return Number.isNaN(parsed) ? null : parsed
}

function millisToDate(millis) {
  if (millis == null) return ''
  const date = new Date(Number(millis))
  if (Number.isNaN(date.getTime())) return ''
  // eslint-disable-next-line no-restricted-syntax -- coupon expiry is stored as a UTC instant
  return date.toISOString().slice(0, 10)
}

function StatusBadge({ status }) {
  const active = status !== 'archived'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
      active
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
    }`}>
      {active ? 'active' : 'archived'}
    </span>
  )
}

function CouponModal({ open, coupon, prices, onClose, onStaged }) {
  const [form, setForm] = useState({})
  const [error, setError] = useState(null)
  const isCreate = !coupon

  useEffect(() => {
    if (!open) return
    setError(null)
    if (coupon) {
      setForm({
        id: coupon.id,
        code: coupon.code || coupon.id,
        name: coupon.name || '',
        discount_type: coupon.discount_type || 'percentage',
        discount_percentage: coupon.discount_percentage ?? '',
        discountDollars: coupon.discount_amount != null ? coupon.discount_amount / 100 : '',
        duration_type: coupon.duration_type || 'one_time',
        period: coupon.period ?? 1,
        period_unit: coupon.period_unit || 'month',
        valid_till: millisToDate(coupon.valid_till),
        max_redemptions: coupon.max_redemptions ?? '',
        item_price_ids: coupon.item_price_ids || [],
      })
    } else {
      setForm({
        id: '',
        code: '',
        name: '',
        discount_type: 'percentage',
        discount_percentage: '',
        discountDollars: '',
        duration_type: 'one_time',
        period: 1,
        period_unit: 'month',
        valid_till: '',
        max_redemptions: '',
        item_price_ids: [],
      })
    }
  }, [open, coupon])

  if (!open) return null

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }))

  const handleStage = () => {
    setError(null)
    try {
      const data = {}

      if (isCreate) {
        const id = form.id?.trim()
        if (!id) throw new Error('A coupon id is required')
        data.id = id
        data.discount_type = form.discount_type
        data.duration_type = form.duration_type
      }

      const code = form.code?.trim()
      if (code) data.code = code
      if (form.name?.trim()) data.name = form.name.trim()

      if (form.discount_type === 'percentage') {
        const percent = Number(form.discount_percentage)
        if (!(percent > 0 && percent <= 100)) {
          throw new Error('Discount must be greater than 0 and at most 100 percent')
        }
        data.discount_percentage = percent
      } else {
        const dollars = Number(form.discountDollars)
        if (!(dollars >= 0)) throw new Error('Discount amount must be zero or more')
        data.discount_amount = Math.round(dollars * 100)
      }

      if (!isCreate) data.duration_type = form.duration_type
      if (form.duration_type === 'limited_period') {
        const period = Number(form.period)
        if (!Number.isInteger(period) || period < 1) {
          throw new Error('Period must be a whole number of at least 1')
        }
        data.period = period
        data.period_unit = form.period_unit
      }

      data.valid_till = form.valid_till ? dateToMillis(form.valid_till) : null

      if (String(form.max_redemptions).trim() === '') {
        data.max_redemptions = null
      } else {
        const cap = Number(form.max_redemptions)
        if (!Number.isInteger(cap) || cap < 1) {
          throw new Error('Redemption limit must be a whole number of at least 1')
        }
        if (!isCreate && cap < (coupon.redemptions ?? 0)) {
          throw new Error(
            `Redemption limit cannot be below the ${coupon.redemptions} already redeemed`
          )
        }
        data.max_redemptions = cap
      }

      data.item_price_ids = form.item_price_ids

      onStaged({
        resource_id: isCreate ? data.id : coupon.id,
        operation: isCreate ? 'create' : 'update',
        original_data: coupon || null,
        data,
      })
      onClose()
    } catch (err) {
      setError(err.message)
    }
  }

  const togglePrice = (priceId) => {
    const current = form.item_price_ids || []
    set({
      item_price_ids: current.includes(priceId)
        ? current.filter((p) => p !== priceId)
        : [...current, priceId],
    })
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isCreate ? 'New Coupon' : 'Edit Coupon'}</DialogTitle>
          <DialogDescription className="font-mono text-xs break-all">
            {isCreate ? 'Staged as a draft until you publish a release' : coupon.id}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {isCreate && (
            <div className="space-y-1.5">
              <Label htmlFor="coupon-id">Coupon ID</Label>
              <Input
                id="coupon-id"
                value={form.id || ''}
                onChange={(e) => set({ id: e.target.value })}
                className="font-mono text-sm"
                placeholder="SUMMER25"
              />
              <p className="text-xs text-muted-foreground">
                Must be unique. Used as the redemption code unless you set one below.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="coupon-code">Code customers type</Label>
              <Input
                id="coupon-code"
                value={form.code || ''}
                onChange={(e) => set({ code: e.target.value })}
                className="font-mono text-sm"
                placeholder={form.id || 'SUMMER25'}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="coupon-name">Internal name</Label>
              <Input
                id="coupon-name"
                value={form.name || ''}
                onChange={(e) => set({ name: e.target.value })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="coupon-discount-type">Discount type</Label>
              <Select
                value={form.discount_type}
                onValueChange={(v) => set({ discount_type: v })}
                disabled={!isCreate}
              >
                <SelectTrigger id="coupon-discount-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DISCOUNT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              {!isCreate && (
                <p className="text-xs text-muted-foreground">
                  Fixed at creation — switching it would change what every future
                  redemption is worth.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="coupon-value">
                {form.discount_type === 'percentage' ? 'Percent off' : 'Amount off (USD)'}
              </Label>
              {form.discount_type === 'percentage' ? (
                <Input
                  id="coupon-value"
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={form.discount_percentage ?? ''}
                  onChange={(e) => set({ discount_percentage: e.target.value })}
                />
              ) : (
                <Input
                  id="coupon-value"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.discountDollars ?? ''}
                  onChange={(e) => set({ discountDollars: e.target.value })}
                />
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5 col-span-1">
              <Label htmlFor="coupon-duration">Applies to</Label>
              <Select value={form.duration_type} onValueChange={(v) => set({ duration_type: v })}>
                <SelectTrigger id="coupon-duration"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {DURATION_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>{DURATION_LABEL[t]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.duration_type === 'limited_period' && (
              <>
                <div className="space-y-1.5">
                  <Label htmlFor="coupon-period">For</Label>
                  <Input
                    id="coupon-period"
                    type="number"
                    min="1"
                    value={form.period ?? 1}
                    onChange={(e) => set({ period: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="coupon-period-unit">Unit</Label>
                  <Select value={form.period_unit} onValueChange={(v) => set({ period_unit: v })}>
                    <SelectTrigger id="coupon-period-unit"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PERIOD_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="coupon-expiry">Expires</Label>
              <Input
                id="coupon-expiry"
                type="date"
                value={form.valid_till || ''}
                onChange={(e) => set({ valid_till: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">Leave blank for no expiry.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="coupon-max">Redemption limit</Label>
              <Input
                id="coupon-max"
                type="number"
                min="1"
                value={form.max_redemptions ?? ''}
                onChange={(e) => set({ max_redemptions: e.target.value })}
                placeholder="Unlimited"
              />
              {!isCreate && (
                <p className="text-xs text-muted-foreground">
                  {coupon.redemptions ?? 0} already redeemed.
                </p>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Limit to specific prices</Label>
            <p className="text-xs text-muted-foreground">
              Leave all unchecked to let the coupon apply to anything.
            </p>
            <div className="max-h-40 overflow-y-auto border rounded-md divide-y">
              {prices.length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground">No prices in this catalog.</div>
              ) : prices.map((price) => (
                <label key={price.id} className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-muted/40">
                  <input
                    type="checkbox"
                    checked={(form.item_price_ids || []).includes(price.id)}
                    onChange={() => togglePrice(price.id)}
                  />
                  <span className="font-mono text-xs truncate">{price.id}</span>
                  <span className="ml-auto text-muted-foreground text-xs">
                    ${((price.price || 0) / 100).toFixed(2)}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 text-sm text-destructive bg-destructive/10 rounded-md p-2.5">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleStage}>Stage change</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Subscription status, which is NOT the coupon status shape.
 *
 * StatusBadge above is binary (active / archived) because that is all a coupon
 * has. Reusing it here would paint a cancelled or paused subscription green and
 * label it "active", so subscription statuses get their own badge that shows
 * the real value.
 */
function SubscriptionStatusBadge({ status }) {
  const tone =
    status === 'active' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
    : status === 'cancelled' ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
    : status === 'paused' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
    : status === 'non_renewing' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
    : 'bg-muted text-muted-foreground'

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tone}`}>
      {status || 'unknown'}
    </span>
  )
}

/**
 * Who redeemed a coupon: the subscriptions it was applied to, and the customer
 * behind each.
 *
 * The empty state deliberately distinguishes two very different situations. If
 * the backend matched a link field but found nothing, the coupon genuinely went
 * unused. If it matched *nothing anywhere*, that is more likely a schema
 * mismatch than a real zero, and saying "no redemptions" would be a lie — so it
 * names the fields it checked instead.
 */
function CouponUsagePanel({ state }) {
  if (!state || state.loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading redemptions…
      </div>
    )
  }

  if (state.error) {
    return (
      <div className="flex items-start gap-2 py-3 text-sm">
        <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
        <div>
          <div className="font-medium">Couldn't load redemptions</div>
          <div className="text-xs text-muted-foreground">{state.error}</div>
        </div>
      </div>
    )
  }

  const { subscriptions = [], summary = {}, matched_via = [], probed = [], truncated } = state.data || {}

  if (!subscriptions.length) {
    const nothingMatched = matched_via.length === 0
    return (
      <div className="flex items-start gap-2 py-3 text-sm text-muted-foreground">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          {nothingMatched ? (
            <>
              <div>No coupon links found in any known location.</div>
              <div className="text-xs mt-1">
                Checked {probed.join(', ')}. If redemptions exist, Manifold records them
                somewhere else and the lookup needs that path added.
              </div>
            </>
          ) : (
            <div>This coupon has not been redeemed yet.</div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
          <Users className="h-3.5 w-3.5" />
          {summary.customer_count} customer{summary.customer_count === 1 ? '' : 's'}
        </span>
        <span>{summary.subscription_count} subscription{summary.subscription_count === 1 ? '' : 's'}</span>
        <span>{summary.active_count} active</span>
        {truncated && <span className="text-amber-600 dark:text-amber-400">showing first {subscriptions.length}</span>}
      </div>

      <div className="overflow-x-auto rounded border bg-background">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-left uppercase text-muted-foreground">
              <th className="px-3 py-2 font-medium">Customer</th>
              <th className="px-3 py-2 font-medium">Email</th>
              <th className="px-3 py-2 font-medium">Plan</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Started</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {subscriptions.map(sub => (
              <tr key={sub.subscription_id} className="hover:bg-muted/30">
                <td className="px-3 py-2">{sub.customer.name || '—'}</td>
                <td className="px-3 py-2 text-muted-foreground">{sub.customer.email || '—'}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  <span className="truncate block max-w-[220px]" title={sub.plan || ''}>{sub.plan || '—'}</span>
                </td>
                <td className="px-3 py-2"><SubscriptionStatusBadge status={sub.status} /></td>
                <td className="px-3 py-2 text-muted-foreground tabular-nums">
                  {sub.started_at ? new Date(sub.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default function CouponsPage() {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  const [modal, setModal] = useState(null)
  // couponId → { loading, error, data } for the expanded usage panels. Kept per
  // coupon so several can be open at once and each remembers what it fetched.
  const [usage, setUsage] = useState({})
  const [expanded, setExpanded] = useState(() => new Set())

  const { changes, addChange, updateChange, findChangeForResource } = useDraftStore()

  /**
   * Expand a coupon and load who redeemed it.
   *
   * Fetched on first expand rather than with the coupon list: this is a Mongo
   * scan per coupon, and most rows never get opened.
   */
  const toggleExpanded = useCallback(async (couponId) => {
    let shouldFetch = false
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(couponId)) {
        next.delete(couponId)
      } else {
        next.add(couponId)
        shouldFetch = true
      }
      return next
    })
    if (!shouldFetch) return

    setUsage(prev => (prev[couponId]?.data ? prev : { ...prev, [couponId]: { loading: true } }))
    try {
      const res = await manifoldAPI.getCouponUsage(couponId)
      setUsage(prev => ({ ...prev, [couponId]: { loading: false, data: res.data?.data || res.data } }))
    } catch (err) {
      setUsage(prev => ({
        ...prev,
        [couponId]: {
          loading: false,
          error: err.response?.data?.error?.message || err.message || 'Failed to load coupon usage',
        },
      }))
    }
  }, [])

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await manifoldAPI.getCatalog()
      setData(res.data?.data || res.data)
    } catch (err) {
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  const canEdit = !!data?.pricing_enabled && data?.pricing_mode === 'release'
  const coupons = data?.coupons

  // Every price in the catalog, flattened, for the scope picker.
  const prices = (data?.catalog || []).flatMap((family) =>
    (family.items || []).flatMap((item) => item.prices || [])
  )

  const pendingCoupons = changes.filter((c) => c.resource_type === 'coupon')

  /**
   * Stage a coupon change, folding it into any change already pending for the
   * same coupon.
   *
   * Manifold rejects a changeset that touches the same document twice, and that
   * would only surface at publish time — so a second edit merges rather than
   * queueing. Merging is a field-wise overlay, which is why archiving is
   * expressed as a status update: as a separate `archive` operation it would
   * replace the pending edit instead of adding to it.
   */
  const stage = ({ resource_id, operation, original_data, data: newData }) => {
    const description = `Coupon ${resource_id}`
    const pending = findChangeForResource('coupon', resource_id)
    if (pending) {
      updateChange(pending.id, {
        // A pending create stays a create — it does not exist to be updated yet.
        operation: pending.operation === 'create' ? 'create' : operation,
        new_data: { ...pending.new_data, ...newData },
        description,
      })
    } else {
      addChange({
        resource_type: 'coupon',
        resource_id,
        operation,
        original_data,
        new_data: newData,
        description,
      })
    }
  }

  const toggleArchive = (coupon) => {
    stage({
      resource_id: coupon.id,
      operation: 'update',
      original_data: coupon,
      data: { status: coupon.status === 'archived' ? 'active' : 'archived' },
    })
  }

  /** How a coupon will read once the pending change is published. */
  const pendingFor = (couponId) => pendingCoupons.find((c) => c.resource_id === couponId)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading coupons...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="flex items-center gap-2 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" /> Retry
        </Button>
      </div>
    )
  }

  // `coupons` is null on the legacy deployment, which has no coupon write path.
  if (coupons === null || coupons === undefined) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          <Tag className="h-8 w-8 mx-auto mb-3 opacity-40" />
          <p>Coupons are not available for this clinic.</p>
          <p className="text-sm mt-1">
            Only clinics on the multitenant Manifold deployment manage coupons here.
          </p>
        </CardContent>
      </Card>
    )
  }

  const query = search.trim().toLowerCase()
  // How many the archived filter is holding back, so the header can say so
  // rather than leaving the gap between "12 coupons" and 9 rows unexplained.
  const hiddenArchived = showArchived ? 0 : coupons.filter((c) => c.status === 'archived').length
  const visible = coupons
    .filter((c) => showArchived || c.status !== 'archived')
    .filter((c) => !query
      || (c.id || '').toLowerCase().includes(query)
      || (c.code || '').toLowerCase().includes(query)
      || (c.name || '').toLowerCase().includes(query))
    .sort((a, b) => (a.code || a.id || '').localeCompare(b.code || b.id || ''))

  // Coupons that only exist as a staged create have nothing to show in the
  // table below, so list them separately rather than leaving them invisible.
  const stagedCreates = pendingCoupons.filter((c) => c.operation === 'create')

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Coupons</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {/* Counts what is on screen, not what exists. These differ whenever
                a filter is on, and a header that silently reported the larger
                number made the archived filter look broken. */}
            {visible.length} coupon{visible.length === 1 ? '' : 's'} in this clinic's Manifold catalog
            {hiddenArchived > 0 && (
              <button
                onClick={() => setShowArchived(true)}
                className="ml-1 underline underline-offset-2 hover:text-foreground"
              >
                · {hiddenArchived} archived hidden
              </button>
            )}
            {canEdit && (
              <span className="ml-2 text-emerald-600 dark:text-emerald-400">
                · edits publish with a release
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          {canEdit && (
            <Button size="sm" onClick={() => setModal({ coupon: null })}>
              <Plus className="h-4 w-4 mr-2" /> New Coupon
            </Button>
          )}
        </div>
      </div>

      {pendingCoupons.length > 0 && (
        <div className="flex items-center justify-between gap-4 p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950">
          <div className="text-sm">
            <span className="font-medium text-amber-900 dark:text-amber-100">
              {pendingCoupons.length} unpublished coupon{' '}
              {pendingCoupons.length === 1 ? 'change' : 'changes'}
            </span>
            <span className="text-amber-800 dark:text-amber-200 ml-2">
              — the list below still shows what Manifold is serving today.
            </span>
          </div>
          <Button size="sm" variant="outline" onClick={() => navigate('/releases')}>
            Review &amp; publish
          </Button>
        </div>
      )}

      <div className="flex items-center gap-4">
        <input
          type="text"
          placeholder="Search by code, id, or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 max-w-md px-4 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Show archived
        </label>
      </div>

      {stagedCreates.length > 0 && (
        <Card>
          <CardContent className="py-4">
            <p className="text-sm font-medium mb-2">Staged new coupons</p>
            <ul className="text-sm space-y-1">
              {stagedCreates.map((c) => (
                <li key={c.id} className="font-mono text-xs text-muted-foreground">
                  {c.new_data?.code || c.resource_id} — {formatDiscount(c.new_data || {})}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {visible.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {search ? `No coupons matching "${search}"` : 'No coupons yet'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="w-8 px-2 py-3" />
                    <th className="px-4 py-3 font-medium">Code</th>
                    <th className="px-4 py-3 font-medium">Discount</th>
                    <th className="px-4 py-3 font-medium">Applies to</th>
                    <th className="px-4 py-3 font-medium">Scope</th>
                    <th className="px-4 py-3 font-medium">Redeemed</th>
                    <th className="px-4 py-3 font-medium">Expires</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {visible.map((coupon) => {
                    const pending = pendingFor(coupon.id)
                    const isOpen = expanded.has(coupon.id)
                    return (
                      <Fragment key={coupon.id}>
                      <tr className="group hover:bg-muted/30">
                        <td className="px-2 py-3">
                          <button
                            onClick={() => toggleExpanded(coupon.id)}
                            className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
                            title={isOpen ? 'Hide who redeemed this' : 'Show who redeemed this'}
                            aria-expanded={isOpen}
                          >
                            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-mono">{coupon.code || coupon.id}</span>
                          {coupon.name && coupon.name !== coupon.id && (
                            <span className="block text-xs text-muted-foreground">{coupon.name}</span>
                          )}
                          {pending && (
                            <span className="inline-block mt-1 text-xs px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-100">
                              {pending.operation} staged
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-semibold tabular-nums">{formatDiscount(coupon)}</td>
                        <td className="px-4 py-3 text-muted-foreground">{formatDuration(coupon)}</td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {coupon.item_price_ids?.length
                            ? `${coupon.item_price_ids.length} price${coupon.item_price_ids.length === 1 ? '' : 's'}`
                            : 'Anything'}
                        </td>
                        <td className="px-4 py-3 tabular-nums text-muted-foreground">
                          {formatRedemptions(coupon)}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{formatExpiry(coupon)}</td>
                        <td className="px-4 py-3"><StatusBadge status={coupon.status} /></td>
                        <td className="px-4 py-3 text-right">
                          {canEdit && (
                            <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={() => setModal({ coupon })}
                                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
                                title="Edit coupon"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={() => toggleArchive(coupon)}
                                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted"
                                title={coupon.status === 'archived' ? 'Restore coupon' : 'Archive coupon'}
                              >
                                {coupon.status === 'archived'
                                  ? <ArchiveRestore className="h-3.5 w-3.5" />
                                  : <Archive className="h-3.5 w-3.5" />}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={9} className="bg-muted/20 px-4 py-3">
                            <CouponUsagePanel state={usage[coupon.id]} />
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <CouponModal
        open={!!modal}
        coupon={modal?.coupon || null}
        prices={prices}
        onClose={() => setModal(null)}
        onStaged={stage}
      />
    </div>
  )
}
